import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import apiClient from "utils/apiClient";
import { ENDPOINTS } from "utils/endpoints";
import RecipeAudioList from "./RecipeAudioPlayer";
import { RED } from "assets/theme/brandColors";
import { parseFrac } from "utils/ingredientFormatting";

function Recipe1Page({
  recipeId,
  programId,
  isCompleted,
  onMarkCompleted,
  onRewardDismissed,
  hideHeader,
  servings,
  onServingsChange,
  onData,
  onFormattedIngredients,
  previewOnly,
  headerSlot,
}) {
  const [recipeData, setRecipeData] = useState(null);
  const [collectionData, setCollectionData] = useState({ saved: false, rating: null, note: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!recipeId) return;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const [res, collRes] = await Promise.all([
          apiClient.get(ENDPOINTS.schedule.recipeDetails(recipeId)),
          apiClient.get(ENDPOINTS.collections.recipeInteraction(recipeId)),
        ]);
        const data = res.data;

        // Aggregate all cooking ingredients across recordings (deduplicated by name+unit)
        const ingMap = {};
        data.recordings.forEach((r) => {
          (r.ingredients || []).forEach((ing) => {
            const key = `${ing.ingredient_name.toLowerCase().trim()}@@${(
              ing.unit || ""
            ).toLowerCase()}`;
            if (ingMap[key]) {
              // Both quantities are now numbers so we can sum them
              ingMap[key].quantity = (ingMap[key].quantity || 0) + parseFrac(ing.quantity);
              ingMap[key].rawQuantity = null; // no longer a single raw string
            } else {
              ingMap[key] = {
                ...ing,
                rawQuantity: ing.quantity,
                quantity: parseFrac(ing.quantity) || 0,
              };
            }
          });
        });
        const allIngs = Object.values(ingMap).map((ing) => ({
          ...ing,
          quantity: ing.rawQuantity ?? ing.quantity,
        }));

        // Deduplicated pantry items for "Before you start" (keep first occurrence per name)
        const pantrySeenNames = new Set();
        const pantryItems = [];
        (data.pantry_items || []).forEach((p) => {
          if (!pantrySeenNames.has(p.name)) {
            pantrySeenNames.add(p.name);
            // Spread, don't hand-pick: the agreement fields (countability,
            // subunit) are read by the shared formatter, and a listing that
            // enumerates fields silently drops every one added later.
            pantryItems.push({ ...p });
          }
        });

        // Group pantry items by recording_id (objects with quantity)
        const pantryByRecording = {};
        (data.pantry_items || []).forEach((p) => {
          if (!pantryByRecording[p.recording_id]) pantryByRecording[p.recording_id] = [];
          pantryByRecording[p.recording_id].push({ ...p });
        });

        const rd = {
          recipeTitle: data.title,
          recipeDescription: data.description || "",
          recipeXp: data.xp || 0,
          recipeTime: data.time_in_minutes || 0,
          recipeServings: data.servings || null,
          mealType: data.meal_type || null,
          diet: data.diet || null,
          allIngredients: allIngs,
          pantryItems: pantryItems,
          audios: data.recordings.map((r) => ({
            id: r.id,
            title: r.title,
            src: r.podcast_url,
            description: r.description,
            // The step with its rationale sentence collapsed. Computed by the
            // backend with prose_checks.remainder_after_span() — never
            // reconstruct it here from the offsets, the seam handling is not
            // obvious and must live in exactly one place.
            descriptionCollapsed: r.description_collapsed || null,
            techniques: r.techniques || [],
            rationale: r.rationale || [],
            rawIngredients: r.ingredients || [],
            pantryItems: pantryByRecording[r.id] || [],
          })),
        };
        setRecipeData(rd);
        if (onData) onData({ recipeData: rd, collectionData: collRes.data });
        setCollectionData(collRes.data);
      } catch (err) {
        setError(err.response?.data?.detail || err.message || "Failed to load recipe.");
      } finally {
        setLoading(false);
      }
    })();
  }, [recipeId, programId]);

  // Returns the full completion payload (XP breakdown, completed goals,
  // level-up flags) so the cooking view can celebrate in place rather than
  // leaving it to /dashboard.
  const handleMarkCompleted = async () => {
    try {
      const res = await apiClient.post(ENDPOINTS.schedule.completeRecipe(recipeId, programId));
      const result = {
        ...res.data,
        total_xp: res.data?.total_xp ?? recipeData?.recipeXp ?? 0,
        xp_gained: res.data?.xp_gained ?? recipeData?.recipeXp ?? 0,
      };
      onMarkCompleted(result);
      return result;
    } catch (err) {
      console.error("Failed to mark recipe completed:", err.message);
      // Optimistic fallback — the recipe still reads as done locally, but
      // there is nothing verified to celebrate.
      const fallback = {
        total_xp: recipeData?.recipeXp ?? 0,
        xp_gained: recipeData?.recipeXp ?? 0,
        goal_xp: 0,
        goals_updated: [],
        leveled_up: false,
        failed: true,
      };
      onMarkCompleted(fallback);
      return fallback;
    }
  };

  if (loading) {
    return (
      <Box textAlign="center" py={6}>
        <CircularProgress sx={{ color: RED }} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!recipeData) return null;

  return (
    <RecipeAudioList
      recipeTitle={recipeData.recipeTitle}
      recipeDescription={recipeData.recipeDescription}
      recipeXp={recipeData.recipeXp}
      recipeTime={recipeData.recipeTime}
      defaultServings={recipeData.recipeServings}
      servings={servings}
      onServingsChange={onServingsChange}
      mealType={recipeData.mealType}
      diet={recipeData.diet}
      allIngredients={recipeData.allIngredients}
      pantryItems={recipeData.pantryItems}
      audios={recipeData.audios}
      isCompleted={isCompleted}
      onMarkCompleted={handleMarkCompleted}
      onRewardDismissed={onRewardDismissed}
      programId={programId}
      recipeId={recipeId}
      initialSaved={collectionData.saved}
      initialRating={collectionData.rating}
      initialNote={collectionData.note}
      hideHeader={hideHeader}
      onFormattedIngredients={onFormattedIngredients}
      previewOnly={previewOnly}
      headerSlot={headerSlot}
    />
  );
}

Recipe1Page.propTypes = {
  recipeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  programId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isCompleted: PropTypes.bool.isRequired,
  onMarkCompleted: PropTypes.func.isRequired,
  onRewardDismissed: PropTypes.func,
  hideHeader: PropTypes.bool,
  servings: PropTypes.number,
  onServingsChange: PropTypes.func,
  onData: PropTypes.func,
  onFormattedIngredients: PropTypes.func,
  // Read-only preview: no completion control. See RecipeAudioList.
  previewOnly: PropTypes.bool,
  // Rendered inside the recipe header card, directly under the title. The
  // cooking view puts its repair controls here so the week-lock line ships
  // with them without being the first thing on the screen.
  headerSlot: PropTypes.node,
};

export default Recipe1Page;
